import { isSensitiveAnalyticsValue } from "./analytics-taxonomy";

export const UTM_STORAGE_KEY = "mot-utm-v1";

export type UtmFields = {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
};

const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
] as const satisfies readonly (keyof UtmFields)[];

const MAX_UTM_VALUE_LENGTH = 100;

function truncateUtmValue(value: string): string {
  return value.length > MAX_UTM_VALUE_LENGTH
    ? value.slice(0, MAX_UTM_VALUE_LENGTH)
    : value;
}

/** Drop empty, oversized-after-truncate, email-like, or otherwise sensitive UTM values. */
export function sanitizeUtmValue(raw: string): string | undefined {
  const trimmed = raw.trim();
  // Check sensitivity on the full trimmed value before truncation so a late `@`
  // past char 100 cannot be chopped off to sneak past the email-like filter.
  if (!trimmed || isSensitiveAnalyticsValue(trimmed)) return undefined;
  const truncated = truncateUtmValue(trimmed);
  if (!truncated) return undefined;
  return truncated;
}

function parseUtmFromHref(href: string): UtmFields | null {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return null;
  }

  const fields: UtmFields = {};
  for (const key of UTM_KEYS) {
    const raw = url.searchParams.get(key);
    if (raw !== null && raw !== "") {
      const sanitized = sanitizeUtmValue(raw);
      if (sanitized) fields[key] = sanitized;
    }
  }

  return Object.keys(fields).length > 0 ? fields : null;
}

function normalizeStoredUtm(parsed: unknown): UtmFields | null {
  if (typeof parsed !== "object" || parsed === null) {
    return null;
  }

  const record = parsed as Record<string, unknown>;
  const fields: UtmFields = {};

  for (const key of UTM_KEYS) {
    const value = record[key];
    if (typeof value === "string" && value !== "") {
      const sanitized = sanitizeUtmValue(value);
      if (sanitized) fields[key] = sanitized;
    }
  }

  return Object.keys(fields).length > 0 ? fields : null;
}

export function readStoredUtm(): UtmFields | null {
  try {
    const raw = sessionStorage.getItem(UTM_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    return normalizeStoredUtm(JSON.parse(raw));
  } catch {
    return null;
  }
}

export function captureUtmFromLocation(href: string): UtmFields | null {
  try {
    const existing = readStoredUtm();
    if (existing) {
      return existing;
    }

    const parsed = parseUtmFromHref(href);
    if (!parsed) {
      return null;
    }

    sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(parsed));
    return parsed;
  } catch {
    return null;
  }
}
